import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Sidebar from '../components/Sidebar';
import axios from 'axios';
import './Dashboard.css';

const API = 'https://cctvapi.shopkwality.in/api';

const Dashboard = () => {
  const { admin } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalEnquiries: 0,
    newEnquiries: 0,
    totalQuotations: 0,
    draftQuotations: 0,
    sharedQuotations: 0,
    convertedQuotations: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch enquiries and quotations
      const [enquiriesRes, quotationsRes] = await Promise.all([
        axios.get(`${API}/enquiries`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/quotations`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const enquiries = Array.isArray(enquiriesRes.data) ? enquiriesRes.data : (enquiriesRes.data?.data || []);
      const quotations = Array.isArray(quotationsRes.data) ? quotationsRes.data : (quotationsRes.data?.data || []);

      // Calculate stats
      setStats({
        totalEnquiries: enquiries.length,
        newEnquiries: enquiries.filter(e => e.status === 'new').length,
        totalQuotations: quotations.length,
        draftQuotations: quotations.filter(q => q.status === 'draft').length,
        sharedQuotations: quotations.filter(q => q.status === 'shared').length,
        convertedQuotations: quotations.filter(q => q.status === 'converted').length
      });

      // Recent activity (last 5 quotations and enquiries combined)
      const recentEnquiries = enquiries.slice(0, 3).map(e => ({
        type: 'enquiry',
        id: e.id,
        title: `Enquiry from ${e.customer_name || 'Unknown'}`,
        timestamp: e.created_at,
        status: e.status
      }));

      const recentQuotations = quotations.slice(0, 3).map(q => ({
        type: 'quotation',
        id: q.id,
        title: `Quotation ${q.quote_no}`,
        timestamp: q.created_at,
        status: q.status
      }));

      const combined = [...recentEnquiries, ...recentQuotations]
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, 8);

      setRecentActivity(combined);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, link }) => (
    <Link to={link} className="stat-card" style={{ borderLeftColor: color }}>
      <div className="stat-icon" style={{ background: `${color}20` }}>
        <span style={{ color }}>{icon}</span>
      </div>
      <div className="stat-content">
        <div className="stat-value">{value}</div>
        <div className="stat-title">{title}</div>
      </div>
    </Link>
  );

  const QuickAction = ({ title, description, icon, link, onClick }) => {
    if (link) {
      return (
        <Link to={link} className="quick-action">
          <div className="quick-action-icon">{icon}</div>
          <div className="quick-action-content">
            <div className="quick-action-title">{title}</div>
            <div className="quick-action-desc">{description}</div>
          </div>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Link>
      );
    }

    return (
      <button type="button" onClick={onClick} className="quick-action quick-action-button">
        <div className="quick-action-icon">{icon}</div>
        <div className="quick-action-content">
          <div className="quick-action-title">{title}</div>
          <div className="quick-action-desc">{description}</div>
        </div>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    );
  };

  const promptForEditId = (label, basePath) => {
    const raw = window.prompt(`Enter ${label} ID to edit`);
    const id = raw ? raw.trim() : '';
    if (!id) return;
    navigate(`${basePath}/${id}/edit`);
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-header">
          <div className="container">
            <h1 className="dashboard-title">Welcome back, {admin?.name || 'Admin'}!</h1>
            <p className="dashboard-subtitle">Here's what's happening with your business today</p>
          </div>
        </div>

        <div className="container">
          {loading ? (
            <div className="dashboard-loading">
              <div className="spinner"></div>
              <p>Loading dashboard...</p>
            </div>
          ) : (
            <>
              {/* Stats Grid */}
              <div className="stats-grid">
                <StatCard
                  title="Total Enquiries"
                  value={stats.totalEnquiries}
                  icon="📧"
                  color="#1E90FF"
                  link="/enquiries"
                />
                <StatCard
                  title="New Enquiries"
                  value={stats.newEnquiries}
                  icon="🔔"
                  color="#FF6B6B"
                  link="/enquiries"
                />
                <StatCard
                  title="Total Quotations"
                  value={stats.totalQuotations}
                  icon="📋"
                  color="#2ECC71"
                  link="/quotations"
                />
                <StatCard
                  title="Draft Quotations"
                  value={stats.draftQuotations}
                  icon="✏️"
                  color="#FFD93D"
                  link="/quotations"
                />
                <StatCard
                  title="Shared Quotations"
                  value={stats.sharedQuotations}
                  icon="🔗"
                  color="#9B59B6"
                  link="/quotations"
                />
                <StatCard
                  title="Converted"
                  value={stats.convertedQuotations}
                  icon="✅"
                  color="#27AE60"
                  link="/quotations"
                />
              </div>

              {/* Quick Actions */}
              <div className="section">
                <h2 className="section-title">Quick Actions</h2>
                <div className="quick-actions-grid">
                  <QuickAction
                    title="New Quotation"
                    description="Create a new quotation for a customer"
                    icon="?"
                    link="/quotations/new"
                  />
                  <QuickAction
                    title="Product List"
                    description="View and manage your product catalog"
                    icon="PL"
                    link="/products"
                  />
                  <QuickAction
                    title="Browse for Quotation"
                    description="Pick products for quotations"
                    icon="BQ"
                    link="/products/browse"
                  />
                  <QuickAction
                    title="Add Product"
                    description="Add a new product to inventory"
                    icon="ADD"
                    link="/products/new"
                  />
                  <QuickAction
                    title="Bulk Import"
                    description="Import products from Excel file"
                    icon="IMP"
                    link="/products/bulk-import"
                  />
                  <QuickAction
                    title="Image Enhancer"
                    description="Find and approve better product images"
                    icon="AI"
                    link="/products/image-enhancer"
                  />
                  <QuickAction
                    title="Marketing Controls"
                    description="Control homepage hero and top products"
                    icon="MKT"
                    link="/marketing-controls"
                  />
                  <QuickAction
                    title="Category List"
                    description="View and manage product categories"
                    icon="CL"
                    link="/categories"
                  />
                  <QuickAction
                    title="Add Category"
                    description="Create a new category"
                    icon="ADD"
                    link="/categories/new"
                  />
                  <QuickAction
                    title="Edit Product"
                    description="Jump to edit by product ID"
                    icon="EDIT"
                    onClick={() => promptForEditId('product', '/products')}
                  />
                  <QuickAction
                    title="Edit Category"
                    description="Jump to edit by category ID"
                    icon="EDIT"
                    onClick={() => promptForEditId('category', '/categories')}
                  />
                </div>
              </div>

              {/* Recent Activity */}
              <div className="section">
                <h2 className="section-title">Recent Activity</h2>
                <div className="activity-list">
                  {recentActivity.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-icon">📭</div>
                      <div className="empty-text">No recent activity</div>
                      <div className="empty-subtext">Your recent enquiries and quotations will appear here</div>
                    </div>
                  ) : (
                    recentActivity.map((activity, index) => (
                      <Link
                        key={index}
                        to={activity.type === 'enquiry' ? `/enquiries/${activity.id}` : `/quotations/${activity.id}`}
                        className="activity-item"
                      >
                        <div className="activity-icon">
                          {activity.type === 'enquiry' ? '📧' : '📋'}
                        </div>
                        <div className="activity-content">
                          <div className="activity-title">{activity.title}</div>
                          <div className="activity-time">
                            {new Date(activity.timestamp).toLocaleString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                        <span className={`status-pill status-${activity.status.replace('_', '-')}`}>
                          {activity.status.replace('_', ' ')}
                        </span>
                      </Link>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
